import { Controller, useFormContext } from 'react-hook-form';
import { TextField } from '@mui/material';

import type { TripArrayFieldContext } from '../machines/createTripArrayFieldMachine';
import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';

/** Trip-leg fields synced via `createTripArrayFieldMachine` child actors. */
export type TripArraySyncedFieldKey =
  | 'appointmentDate'
  | 'appointmentDateTime'
  | 'pickupDate'
  | 'pickupDateTime'
  | 'pickupAddress'
  | 'dropoffAddress'
  | 'pickupPhone'
  | 'dropoffPhone'
  | 'pickupNotes'
  | 'dropoffNotes';

export function TripArrayFieldTextField({
  tripField,
  index,
  label,
  type,
  multiline,
  minRows,
  required,
}: {
  tripField: TripArraySyncedFieldKey;
  index: number;
  label: string;
  type?: string;
  multiline?: boolean;
  minRows?: number;
  required?: boolean;
}) {
  const { control } = useFormContext<ReservationFormInput>();
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  const snapshot = useFieldActorSnapshot(actor);
  const values = (snapshot.context as TripArrayFieldContext).values;
  const raw = values[index];
  const value = typeof raw === 'string' ? raw : '';

  return (
    <Controller
      name={`trips.${index}.${tripField}`}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          label={label}
          value={value}
          onChange={(e) => {
            const v = e.target.value;
            actor.send({ type: 'CHANGE', index, value: v });
            field.onChange(v);
          }}
          onBlur={() => {
            actor.send({ type: 'BLUR', index });
            field.onBlur();
          }}
          onFocus={() => actor.send({ type: 'FOCUS', index })}
          name={field.name}
          inputRef={field.ref}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message}
          variant="outlined"
          fullWidth
          type={type}
          multiline={multiline}
          minRows={minRows}
          required={required}
          InputLabelProps={
            type === 'datetime-local' ? { shrink: true } : undefined
          }
        />
      )}
    />
  );
}
